import { ArrowRightIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useApprovals, useCases } from '../api/hooks';
import { ACTION_LABELS, SEVERITY_ORDER, TERMINAL_STATUSES } from '../api/types';
import type { Case } from '../api/types';
import { Card, CardHeader, StatCard } from '../components/Card';
import { Callout } from '../components/Callout';
import { PageHeader } from '../components/Layout';
import { Button } from '../components/Button';
import { EmptyState, QueryBoundary } from '../components/states';
import { ActionStatusBadge, SeverityBadge, StatusBadge } from '../components/DomainBadges';
import { useFormatters } from '../time/format';

export function Dashboard() {
  const navigate = useNavigate();
  const cases = useCases({ status: 'all' });
  const approvals = useApprovals();
  const { relative, dateTime, zone } = useFormatters();

  const stats = useMemo(() => {
    const all = cases.data?.cases ?? [];
    const open = all.filter((c) => !TERMINAL_STATUSES.has(c.status));
    return {
      open: open.length,
      // Severity is the max of a case's alerts, so this counts cases that contain at least
      // one high or critical alert — not an average.
      urgent: open.filter((c) => SEVERITY_ORDER[c.severity] >= SEVERITY_ORDER.high).length,
      awaitingApproval: open.filter((c) => c.status === 'awaiting_approval').length,
      contained: all.filter((c) => c.status === 'contained').length,
    };
  }, [cases.data]);

  const recent = useMemo(() => {
    const all = [...(cases.data?.cases ?? [])];
    // Most recently touched first. `updated_at` rather than `opened_at`: a week-old case that
    // just had a new alert correlated onto it is the one worth looking at.
    all.sort((a, b) => b.updated_at.localeCompare(a.updated_at));
    return all.slice(0, 12);
  }, [cases.data]);

  const pending = approvals.data?.actions ?? [];

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle={`Alert intake, triage and containment at a glance. Times in ${zone}.`}
        actions={
          <Button
            variant="primary"
            size="xs"
            onClick={() => {
              void cases.refetch();
              void approvals.refetch();
            }}
            disabled={cases.isFetching}
          >
            <ArrowPathIcon
              className={`h-3.5 w-3.5 ${cases.isFetching ? 'animate-spin' : ''}`}
              aria-hidden="true"
            />
            Refresh
          </Button>
        }
      />

      <div className="space-y-6">
        {/*
          The approvals queue is the one thing on this page that is blocking work: an action
          sitting here is containment that policy decided needs a human, and it expires
          (approvalTtlHours, 8 by default) rather than waiting indefinitely.
        */}
        {pending.length > 0 ? (
          <Callout
            tone="warning"
            role="alert"
            action={
              <Link
                to="/approvals"
                className="inline-flex items-center gap-1 whitespace-nowrap text-xs font-medium underline"
              >
                Review now
                <ArrowRightIcon className="h-3 w-3" aria-hidden="true" />
              </Link>
            }
          >
            <strong>{pending.length}</strong> containment{' '}
            {pending.length === 1 ? 'action is' : 'actions are'} waiting for approval. Each
            expires if nobody acts.
          </Callout>
        ) : null}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="Open cases"
            value={stats.open}
            hint="Not yet contained or closed"
            onClick={() => navigate('/cases')}
          />
          <StatCard
            label="High or critical"
            value={stats.urgent}
            hint="Open, by highest alert severity"
            tone={stats.urgent > 0 ? 'danger' : undefined}
            valueTone={stats.urgent > 0 ? 'danger' : 'body'}
            onClick={() => navigate('/cases?severity=critical')}
          />
          <StatCard
            label="Awaiting approval"
            value={stats.awaitingApproval}
            hint="Blocked on a second responder"
            tone={stats.awaitingApproval > 0 ? 'warning' : undefined}
            valueTone={stats.awaitingApproval > 0 ? 'warning' : 'body'}
            onClick={() => navigate('/approvals')}
          />
          <StatCard label="Contained" value={stats.contained} hint="Containment completed" />
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader
              title="Recent activity"
              subtitle="Most recently updated cases"
              action={
                <Link
                  to="/cases"
                  className="inline-flex items-center gap-1 text-xs text-brand-text hover:underline"
                >
                  View all
                  <ArrowRightIcon className="h-3 w-3" aria-hidden="true" />
                </Link>
              }
            />
            <QueryBoundary
              isLoading={cases.isPending}
              error={cases.error}
              onRetry={() => void cases.refetch()}
              loadingLabel="Loading cases"
            >
              {recent.length === 0 ? (
                <EmptyState
                  title="No cases yet"
                  detail="Cases appear here as alerts arrive and correlate."
                />
              ) : (
                <ul className="divide-y divide-line">
                  {recent.map((item) => (
                    <CaseRow key={item.case_id} value={item} relative={relative} />
                  ))}
                </ul>
              )}
            </QueryBoundary>
          </Card>

          <Card>
            <CardHeader
              title="Approvals queue"
              subtitle="Containment policy routed to a human"
              action={
                <Link
                  to="/approvals"
                  className="inline-flex items-center gap-1 text-xs text-brand-text hover:underline"
                >
                  Open queue
                  <ArrowRightIcon className="h-3 w-3" aria-hidden="true" />
                </Link>
              }
            />
            <QueryBoundary
              isLoading={approvals.isPending}
              error={approvals.error}
              onRetry={() => void approvals.refetch()}
              loadingLabel="Loading approvals"
            >
              {pending.length === 0 ? (
                <EmptyState
                  title="Nothing waiting"
                  detail="Actions requiring approval appear here the moment the policy gate routes one."
                />
              ) : (
                <ul className="divide-y divide-line">
                  {pending.slice(0, 12).map((action) => (
                    <li key={action.action_id} className="py-3">
                      <Link
                        to={`/cases/${encodeURIComponent(action.case_id)}`}
                        className="-mx-2 block rounded-lg px-2 transition-colors hover:bg-raised"
                      >
                        <div className="mb-1 flex items-center justify-between gap-2">
                          <span className="truncate text-sm font-medium text-body">
                            {ACTION_LABELS[action.kind] ?? action.kind}
                          </span>
                          <ActionStatusBadge status={action.status} />
                        </div>
                        <p className="truncate text-xs text-muted">{action.target}</p>
                        <p className="mt-0.5 text-xs text-subtle">
                          {action.expires_at
                            ? `Expires ${relative(action.expires_at)} · ${dateTime(action.expires_at)}`
                            : `Requested ${relative(action.created_at)}`}
                        </p>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </QueryBoundary>
          </Card>
        </div>
      </div>
    </>
  );
}

function CaseRow({
  value,
  relative,
}: {
  value: Case;
  relative: (input: string) => string;
}) {
  return (
    <li className="py-3">
      <Link
        to={`/cases/${encodeURIComponent(value.case_id)}`}
        className="-mx-2 block rounded-lg px-2 transition-colors hover:bg-raised"
      >
        <div className="mb-1 flex items-center justify-between gap-2">
          <span className="truncate text-sm font-medium text-body">{value.title}</span>
          <SeverityBadge severity={value.severity} />
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={value.status} />
          <span className="truncate font-mono text-[11px] text-subtle">{value.case_id}</span>
          <span className="ml-auto shrink-0 text-xs text-subtle">
            {relative(value.updated_at)}
          </span>
        </div>
      </Link>
    </li>
  );
}
